/* eslint-disable */

import React, { useMemo } from "react";
// THIRD PARTY
import classNames from "classnames";
import PropTypes from "prop-types";

// UTILITIES
import { withABTestWrapper } from "@lululemon/mwa-ab-testing/esm";
import {
  StatelessAccordionV1 as StatelessAccordion,
  AccordionItem,
  CTALink,
  Link,
} from "@lululemon/ui-library";
import { createEncodedKey } from "@lululemon/ecom-common/src/util/helpers";
import { log } from "@lululemon/mwa-utils";
import { useDeviceType } from "@lululemon/ecom-site/src/util/with-device-characteristics";
import { createErrorBoundaryWrapper } from "@lululemon/ecom-cdp/src/lib/cdp-error-boundaries";
import DynamicLinkProvider from "@lululemon/ecom-common/src/components/dynamic-link-provider/index";

// COMPONENTS
import FacetItem from "./components/facet-item/facet-item";
import FilterPills from "./components/filter-pills/filter-pills";
import MobileFacetWrapper from "./components/mobile/mobile-facet-wrapper";

// STYLES
import styles from "./facet-nav.module.scss";

// UTILS
import {
  parseFacetData,
  getDerivedFacetData,
  shouldUseABTGrouping,
} from "./utils";

// SERP AND CDP CONTEXT
import useParseContext from "./hooks/useParseContext";

import ExpandableListWrapper from "./expandable-list-wrapper";
import SizeFacets from "./components/size-facets/size-facets";
import { useExpandedFacetsContext } from "./hooks/useExpandedFacetsContext";

// RENDERS ACCORDIONS
const Facets = React.memo(
  ({
    facetData,
    isHeroVariant,
    extraFacetNavProps,
    truefitAtCDPABFlag,
    cdpEnableUpdatedSizeGroupABFlag,
    category,
    locale,
    categoryTitle,
    path,
  }) => {
    const {
      isMobile,
      isCollectionCarouselMerchandised,
      selectedSortTitle: selectedSortTitleCDPCtx,
      showBraQuizResults,
      braQuizButtonURL,
      shouldShowBraQuizFacet,
      collectionExplainerABFlag,
      cdpCounterABFlag,
    } = useParseContext();

    if (!Array.isArray(facetData) && facetData !== undefined) {
      const errorContext = {
        message: "FacetData is either not an array or undefined",
        facetData: JSON.stringify(facetData),
      };
      const error = new Error(`FacetData Error: ${errorContext.message}`);
      error.componentData = { facetData: errorContext.facetData };
      throw error;
    }

    const expandedFacetCtxVals = useExpandedFacetsContext();
    // The SearchContext HOC is not available in SERP, so useParseContext cannot be used for SERP.
    // selectedSortTitleSerp are used exclusively for the SERP to override useParseContext values.
    const selectedSortTitleSerp = facetData?.[0]?.sortSelectedLabel;
    const selectedSortTitle = selectedSortTitleCDPCtx || selectedSortTitleSerp;

    const onSortMenuSelectClick = extraFacetNavProps?.onSortMenuSelectClick;
    const expandedFacets = extraFacetNavProps?.expandedFacets;

    const { deviceType } = useDeviceType();
    const isTabletOrBelow = deviceType.isTablet || deviceType.isMobile;

    const {
      handleToggleAccordionItem: handleToggleAccordionItemCDPCtx,
      toggleExpandedItems: toggleExpandedItemsCDPCtx,
      isTruncatedPanelExpanded: isTruncatedPanelExpandedCDPCtx,
      expandedAccordionPanels: expandedAccordionPanelsCDPCtx,
    } = expandedFacetCtxVals;

    const handleToggleAccordionItem =
      handleToggleAccordionItemCDPCtx ||
      expandedFacets?.handleToggleAccordionItem;

    const toggleExpandedItems =
      toggleExpandedItemsCDPCtx || expandedFacets?.toggleExpandedItems;

    const isTruncatedPanelExpanded =
      isTruncatedPanelExpandedCDPCtx ||
      expandedFacets?.isTruncatedPanelExpanded;

    const expandedAccordionPanels =
      expandedAccordionPanelsCDPCtx || expandedFacets?.expandedAccordionPanels;

    const { parsedFacetData } = useMemo(
      () => parseFacetData({ facetData }),
      [facetData]
    );

      return (
        <div
          className={classNames(styles.facetNavWrapper, {
            [styles.facetNavWrapperSpacing]: isHeroVariant,
          })}
        >
          <StatelessAccordion
            setAutoHeight={false}
            id="filter-accordion"
            noShadow
            expandedAccordionPanels={expandedAccordionPanels}
            onClickAccordionItem={(index) => handleToggleAccordionItem(index)}
            className={classNames({
              [styles.hideFirstDivider]:
                isCollectionCarouselMerchandised && !cdpCounterABFlag,
              [styles.hideFirstDividerCounter]:
                isCollectionCarouselMerchandised,
            })}
          >
            {parsedFacetData.map((item, key) => {
              const {
                name,
                parsedName,
                refinements,
                enableVisualSizeStyling,
                isTrucatedFilterExpanded,
                shouldDisplayViewMoreButton,
                shouldDisplayTruncatedFilters,
                shouldHideMobileFilterSortButton,
                facetItemProps,
                accordionItemProps,
                isSort,
                viewMoreButtonProps,
                itemLength,
              } = getDerivedFacetData({
                facetItemData: item,
                isMobile,
                isTruncatedPanelExpanded,
                isTabletOrBelow,
                selectedSortTitle,
                collectionExplainerABFlag,
              });

            if (shouldHideMobileFilterSortButton || itemLength === 0) {
              if (itemLength === 0) log.error("No facetItems to render");
              return null;
            }

            const accordionTitleClassName = classNames({
              [styles.filterExpandedTitle]:
                !!expandedAccordionPanels[parsedName],
            });

            const trackEventBraQuizText = showBraQuizResults
              ? "bra-quiz-cta-retake-filter-size"
              : "bra-quiz-cta-filter-size";

            const isSizesNav = name?.toLowerCase() === "size";

            const shouldDisplaySizesWithTitles =
              isSizesNav &&
              cdpEnableUpdatedSizeGroupABFlag &&
              shouldUseABTGrouping(path, locale, category);

            return (
              <AccordionItem
                {...accordionItemProps}
                headingClassName={accordionTitleClassName}
                panelContentClassName={classNames(styles.noPaddingRight, {
                  [styles.sortAccordion]: isSort,
                })}
                className="OneLinkTx"
                key={createEncodedKey({ item, key })}
              >
                {shouldShowBraQuizFacet && isSizesNav && (
                  <div className={classNames(styles.braQuizTextContainer)}>
                    <p
                      className={classNames(
                        "lll-text-primary",
                        styles.braQuizText
                      )}
                    >
                      Not sure about your size?
                    </p>
                    {truefitAtCDPABFlag && (
                      <Link
                        className="tfc-fitrec-register"
                        data-category="Bras"
                        data-locale="en_US"
                        direction="right"
                        tag="div"
                        variant="arrowUnderline"
                      >
                        {showBraQuizResults
                          ? "Retake the bra quiz?"
                          : "Take the bra quiz"}
                      </Link>
                    )}
                    {!truefitAtCDPABFlag && (
                      // <DynamicLinkProvider> TODO: Uncomment once CDP and BraQuiz is migrated to v3
                        <CTALink
                          kind="secondary"
                          href={braQuizButtonURL}
                          data-lulu-track={trackEventBraQuizText}
                        >
                          {showBraQuizResults
                            ? "Retake the bra quiz?"
                            : "Take the bra quiz"}
                        </CTALink>
                      // </DynamicLinkProvider> TODO: Uncomment once CDP and BraQuiz is migrated to v3
                    )}
                  </div>
                )}
                <ExpandableListWrapper
                  primaryNumRefinements={
                    facetItemProps?.primaryNumRefinements
                  }
                  isTabletOrBelow={isTabletOrBelow}
                  shouldDisplayViewMoreButton={shouldDisplayViewMoreButton}
                  isTruncatedFilterExpanded={isTrucatedFilterExpanded}
                  buttonProps={{
                    viewMoreButtonProps,
                    shouldDisplayTruncatedFilters,
                    parsedName,
                    toggleExpandedItems,
                  }}
                  className={classNames("row lll-base", "OneLinkTx", {
                    [styles.noMargin]: enableVisualSizeStyling,
                    [styles.truncatedContainer]:
                      shouldDisplayTruncatedFilters,
                  })}
                >
                  {isSizesNav ? (
                    <SizeFacets
                      shouldRenderWithTitles={shouldDisplaySizesWithTitles}
                      refinements={refinements}
                      enableVisualSizeStyling={enableVisualSizeStyling}
                      shouldDisplayTruncatedFilters={
                        shouldDisplayTruncatedFilters
                      }
                      facetItemProps={facetItemProps}
                      onSortMenuSelectClick={onSortMenuSelectClick}
                      locale={locale}
                      category={category}
                      path={path}
                      categoryTitle={categoryTitle}
                    />
                  ) : (
                    refinements?.map((refinement, i) => (
                      <FacetItem
                        {...facetItemProps}
                        onSortMenuSelectClick={onSortMenuSelectClick}
                        refinement={refinement}
                        index={i}
                        key={createEncodedKey({
                          ...refinement,
                          i,
                        })}
                      />
                    ))
                  )}
                </ExpandableListWrapper>
              </AccordionItem>
            );
          })}
        </StatelessAccordion>
      </div>
    );
  }
);

// MAIN WRAPPER FOR FACETNAV
const FacetNav = ({
  facetData,
  setRef,
  abTestCollectionSubtitle,
  isHeroVariant,
  truefitAtCDPABFlag,
  cdpEnableUpdatedSizeGroupABFlag,
  category,
  locale,
  path,
  categoryTitle,
}) => {
  const {
    title,
    filterPill,
    facets,
    removeAction = "",
    appliedFiltersNumber,
  } = facetData;

  return (
    <nav>
      <MobileFacetWrapper
        title={title}
        facetData={facetData}
        abTestCollectionSubtitle={abTestCollectionSubtitle}
        removeAction={removeAction}
        pillDataLength={appliedFiltersNumber}
      >
        <FilterPills pillRef={setRef} pillData={filterPill} />
        <Facets
          facetData={facets}
          extraFacetNavProps={facetData.extraFacetNavProps}
          isHeroVariant={isHeroVariant}
          truefitAtCDPABFlag={truefitAtCDPABFlag}
          cdpEnableUpdatedSizeGroupABFlag={cdpEnableUpdatedSizeGroupABFlag}
          category={category}
          locale={locale}
          path={path}
          categoryTitle={categoryTitle}
        />
      </MobileFacetWrapper>
    </nav>
  );
};

export { FacetNav };

const FacetNavWithABTest = withABTestWrapper({
  name: "site-facet-nav",
  abTestEndDate: "01/01/2024",
})(FacetNav);

export default FacetNavWithABTest;

export const CDPFacetNavWithErrorBoundary = createErrorBoundaryWrapper(
  FacetNavWithABTest,
  "FacetNav"
);

Facets.displayName = "Facets";
FacetNav.displayName = "FacetNav";

FacetNav.defaultProps = {
  facetData: {
    title: "",
    filterPill: [],
    facets: [],
    extraFacetNavProps: {},
  },
  setRef: {},
  truefitAtCDPABFlag: false,
  cdpEnableUpdatedSizeGroupABFlag: false,
  category: "",
  locale: "",
  path: "",
  categoryTitle: "",
};

FacetNav.propTypes = {
  facetData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    filterPill: PropTypes.arrayOf(PropTypes.object),
    facets: PropTypes.arrayOf(PropTypes.object),
    extraFacetNavProps: PropTypes.shape({
      isMobileFacetNavOpen: PropTypes.bool,
      totalProductsCount: PropTypes.number,
      onToggleMobileFacetNavClick: PropTypes.func,
      onSortMenuSelectClick: PropTypes.func,
    }),
  }),
  setRef: PropTypes.object,
  truefitAtCDPABFlag: PropTypes.bool,
  cdpEnableUpdatedSizeGroupABFlag: PropTypes.bool,
  category: PropTypes.string,
  locale: PropTypes.string,
  path: PropTypes.string,
  categoryTitle: PropTypes.string,
};

Facets.propTypes = {
  facetData: PropTypes.arrayOf(PropTypes.object).isRequired,
};

FilterPills.defaultProps = {
  filterPillCallback: () => { },
};

FilterPills.propTypes = {
  filterPillCallback: PropTypes.func.isRequired,
};
